/* @refresh reload */
/**
 * Startup order is the contract here (web-plan.md F0/F1/R45):
 *
 * 1. the icon face, then the emoji face WITH its unicode-range allow-list
 *    (D166/D208 — the face may only ever cover the reaction vocabulary),
 *    then the shared stylesheet, then the web-only fallbacks;
 * 2. wasm — every rule both ends enforce loads before the first render;
 * 3. the Service Worker (D47) — and the shell waits for `ready` before it
 *    mounts, so the first feed's avatars go out with a bearer (R45);
 * 4. render.
 */
import "./styles/fonts.css";
import "../../shared/app.css";
import "./styles/web-compat.css";
// The selection mark that rolls between the tabs of a strip and the rows
// of the sidebar. Shared with the desktop client for the reason the
// stylesheet is (D57): one script measuring one piece of markup. It wires
// itself to every `[data-ink]` host it finds, now and later, so there is
// nothing to call — the import is the whole of it.
import "../../shared/ink.js";

import { render } from "solid-js/web";

import emojiRange from "../../shared/fonts/echo-emoji.range?raw";
import emojiFontUrl from "../../shared/fonts/echo-emoji.woff2?url";

import { App } from "./app";
import { initWasm } from "./lib/wasm";

function injectEmojiFace(): void {
  // The face is 1.7 MB and bound to exactly four selectors in the shared
  // sheet (.reaction-chip__emoji, .quick-reactions__cell, .emoji-panel__glyph, .emoji-panel__tab); the
  // range keeps every out-of-vocabulary character on the system stack.
  const style = document.createElement("style");
  style.textContent = `@font-face{font-family:"Echo Emoji";src:url(${JSON.stringify(
    emojiFontUrl,
  )}) format("woff2");font-display:block;unicode-range:${emojiRange.trim()}}`;
  document.head.appendChild(style);
}

async function start(): Promise<void> {
  injectEmojiFace();

  if ("serviceWorker" in navigator) {
    try {
      await navigator.serviceWorker.register("/sw.js");
      // R45: media requests before the worker controls the page would go
      // out without a bearer; `ready` + clients.claim() closes the window.
      await navigator.serviceWorker.ready;
    } catch {
      // No worker (private mode, unsupported): avatars 401 visibly and the
      // rest of the client works — the honest degradation.
    }
  }

  await initWasm();

  const root = document.getElementById("main");
  if (!root) throw new Error("index.html lost its #main");
  render(() => <App />, root);
}

void start();
